// SuccessModal.jsx - displays email sending success information
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "components/mui";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { formatScheduleDate } from 'utils/canmail/formatScheduleDate';

function SuccessModal({ open, onClose, emailsSucceeded, emailsAttempted, isScheduled, scheduleDate, mode = 'send' }) {
  // Determine modal title based on mode
  const getTitle = () => {
    if (mode === 'update') return "Scheduled Email Updated!";
    if (mode === 'schedule') return "Email Scheduled!";
    return "Email Sent Successfully!"; // mode === 'send' (includes both regular send and Send Now)
  };

  // Determine modal content based on mode.
  // Note the sibling <Typography> elements rather than nesting: a Typography renders a
  // <p> by default, and a <p> inside a <p> is invalid DOM that React will warn about
  // and browsers will silently restructure.
  const getContent = () => {
    if (mode === 'update') {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1">
            Your changes have been saved.<br />The email will be sent on{" "}
            <strong>{formatScheduleDate(scheduleDate)}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The updated email will be sent automatically at the scheduled time.
          </Typography>
        </Box>
      );
    }

    if (mode === 'schedule') {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1">
            Your email has been scheduled for{" "}
            <strong>{formatScheduleDate(scheduleDate)}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            It will be sent automatically at the scheduled time.
          </Typography>
        </Box>
      );
    }

    // mode === 'send' (regular immediate send or Send Now)
    return (
      <Typography variant="body1">
        Successfully queued <strong>{emailsSucceeded}</strong> out of{" "}
        <strong>{emailsAttempted}</strong> recipients.
      </Typography>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <CheckCircleOutlineIcon sx={{ color: "success.main", fontSize: 28 }} />
          <Typography variant="h6" color="success.main">
            {getTitle()}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ textAlign: "center", py: 2 }}>
        {getContent()}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          To see email history or reschedule emails, click on Email History
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{ minWidth: 120 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SuccessModal;
